import { ActivityResponse } from '@/types/benefits/ActivityResponse';
import { CURRENCIES } from '../../../../../shared/CountriesData';
import { LOCALES } from '@/i18n/config';
import { SupportedLocale } from '@/types/supported-locale.type';
import { numberIfNotUndefined } from '../../prices/numberIfNotUndefined';

const initializeActivityTranslations = (activity: ActivityResponse) => {
  const translations: Record<
    SupportedLocale,
    {
      shortDescription?: string;
      longDescription?: string;
      benefit?: { name?: string };
    }
  > = {} as Record<
    SupportedLocale,
    {
      shortDescription?: string;
      longDescription?: string;
      benefit?: { name?: string };
    }
  >;

  for (const locale of LOCALES) {
    translations[locale] = {
      shortDescription:
        activity.translations?.[locale]?.shortDescription ||
        activity.shortDescription ||
        '',
      longDescription:
        activity.translations?.[locale]?.longDescription ||
        activity.longDescription ||
        '',
      benefit: {
        name:
          activity.translations?.[locale]?.benefit?.name ||
          activity.benefit.name ||
          '',
      },
    };
  }

  return translations;
};

export const loadActivityDefaultValues = (activity: ActivityResponse) =>
  ({
    name: activity.benefit.name,
    supplier: {
      label: activity.benefit.supplier.name,
      value: activity.benefit.supplier.id,
    },
    currency: activity.benefit.currency
      ? {
          value: activity.benefit.currency,
          label: CURRENCIES.find(
            (currency) => currency.value === activity.benefit.currency,
          )?.label,
        }
      : undefined,
    country: activity.benefit.country
      ? {
          label: activity.benefit.country,
          value: activity.benefit.country,
        }
      : undefined,
    city: activity.city
      ? { label: activity.city, value: activity.city }
      : undefined,
    marginRate: numberIfNotUndefined(activity.benefit.marginRate),
    shortDescription: activity.shortDescription ?? undefined,
    longDescription: activity.longDescription ?? undefined,
    images: activity.images ?? [],
    tarifType:
      activity.pricingType === 'byBenefit' ? 'par-prestation' : 'par-personne',
    customTarif:
      activity.specialTarif !== undefined
        ? activity.specialTarif
          ? 'true'
          : 'false'
        : 'false',
    childAgeFrom: activity.childAgeFrom ?? undefined,
    childAgeTo: activity.childAgeTo ?? undefined,
    translations: initializeActivityTranslations(activity),
    tarifs:
      activity.benefit.Pricing.length > 0
        ? activity.benefit.Pricing.map((tarif) => ({
            name: tarif.name ?? undefined,
            periods: tarif.periods.map((period) => ({
              startDate: period.startDate
                ? new Date(period.startDate)
                : undefined,
              endDate: period.endDate ? new Date(period.endDate) : undefined,
              applicationDays: period.applicationDays,
            })),
            pricePartitions: tarif.pricePartitions
              .toSorted((a, b) => (a.from ?? 0) - (b.from ?? 0))
              .map((partition) => ({
                price: numberIfNotUndefined(partition.price),
                from: partition.from ? Number(partition.from) : undefined,
                to: partition.to ? Number(partition.to) : undefined,
                specialPrice: numberIfNotUndefined(partition.specialPrice),
              })),
          }))
        : [
            {
              name: undefined,
              periods: [
                {
                  startDate: undefined,
                  endDate: undefined,
                  applicationDays: [],
                },
              ],
              pricePartitions: [
                {
                  price: undefined,
                  from: 1,
                  to: 1,
                },
              ],
            },
          ],
  }) as const;
